import {
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  MenuItem,
  Autocomplete,
} from '@mui/material'
import './ActionBar.css'
import { DeleteOutlined, EditOutlined, LayersOutlined, PriceChangeOutlined } from '@mui/icons-material'
import { quoteLineType } from '../types'
import { useState } from 'react'
import { flattenBundledStructure } from '../helper'
import { CurrencyInput } from '@impres-development/assets/client'
import { LineEdit } from '../EditQuote/lines/LineEdit'
import { useParams } from 'react-router-dom'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { useQuoteLines } from 'src/EditQuote/lines/QuoteLinesContext'
import { BundleLineItem } from 'src/EditQuote/lines/lineItemPricing'

type BundleSelection = BundleLineItem | string | null

const getBundleLabel = (option: BundleLineItem | string) =>
  typeof option === 'string' ? option : option.MfrPartNumber ?? ''

const isSameBundleOption = (option: BundleLineItem | string, value: BundleSelection) =>
  typeof option === 'string' || typeof value === 'string'
    ? option === value
    : value !== null && option.LineItemId === value.LineItemId

export default function ActionBar({
  selectedLineItems,
  setSelectedLineItems,
  lineItemCount,
}: {
  selectedLineItems: quoteLineType[]
  setSelectedLineItems: (lineItems: quoteLineType[]) => void
  lineItemCount: number
}) {
  const [openDialog, setOpenDialog] = useState<'price' | 'bundle' | 'edit' | 'delete' | null>(null)
  const { quoteId } = useParams()
  const { lineItems, handleBulkEdit, handleBulkDelete, handleBundle } = useQuoteLines()
  const [amount, setAmount] = useState<number>(0)
  const [pricingMethod, setPricingMethod] = useState<string | null>(null)
  const [bundleSelection, setBundleSelection] = useState<BundleSelection>(null)
  const [bundleDescription, setBundleDescription] = useState<string>('')
  const bundleOptions = lineItems.filter(item => item.LineNumber === -1)
  const selectedBundle = typeof bundleSelection === 'object' ? bundleSelection : null
  const isAddingToExistingBundle = !!selectedBundle?.Bundle

  const resetBundleDialog = () => {
    setBundleSelection(null)
    setBundleDescription('')
  }

  const handleBundleSelectionChange = (_: unknown, value: BundleSelection) => {
    setBundleSelection(value)
    if (value && typeof value !== 'string') {
      setBundleDescription(value.Description ?? '')
    } else if (!value) {
      setBundleDescription('')
    }
  }

  const handleDelete = (lineItemsToDelete: quoteLineType[]) => {
    const itemsToDeleteIds = new Set(lineItemsToDelete.map(item => item.LineItemId))

    const bundlesToCheck = new Set(lineItemsToDelete.filter(item => item.Bundle).map(item => item.Bundle)) as Set<
      string | null
    >
    bundlesToCheck.delete(null)

    for (const bundleId of bundlesToCheck) {
      if (!bundleId) continue
      const bundleInStructure = lineItems.find(item => item.Bundle === bundleId && item.LineNumber === -1)
      if (bundleInStructure) {
        const childrenInBundle = bundleInStructure.children
        const childrenToDelete = lineItemsToDelete.filter(item => item.Bundle === bundleId && item.LineNumber !== -1)
        if (childrenToDelete.length === (childrenInBundle?.length ?? 0)) {
          itemsToDeleteIds.add(bundleInStructure.LineItemId)
        }
      }
    }

    handleBulkDelete(Array.from(itemsToDeleteIds))
      .then(() => setSelectedLineItems([]))
      .catch(() => {
        // error already logged and surfaced via bulkDeleteSnackbar in handleBulkDelete
      })
  }

  const handleEditPrice = (amount: number) => {
    const flatAllItems = flattenBundledStructure(lineItems)
    const selectedLineItemIds = new Set(selectedLineItems.map(item => item.LineItemId))

    const updatedLineItems = flatAllItems.map(item => {
      if (selectedLineItemIds.has(item.LineItemId) && item.LineNumber !== -1) {
        let updatedItem = { ...item }

        if (pricingMethod === 'Cost + %') {
          const unitPrice = (item.UnitCost ?? 0) * (1 + amount / 100)
          updatedItem = {
            ...updatedItem,
            UnitPrice: unitPrice,
          }
        } else if (pricingMethod === 'Cost + $') {
          const unitPrice = (item.UnitCost ?? 0) + amount
          updatedItem = {
            ...updatedItem,
            UnitPrice: unitPrice,
          }
        } else if (pricingMethod === 'Gross Profit (GP)') {
          const qty = item.Qty ?? 0
          const costWithFees = qty * (item.UnitCost ?? 0) + (item.TotalFees ?? 0)
          const unitPrice = costWithFees / (1 - amount / 100) / qty
          updatedItem = {
            ...updatedItem,
            UnitPrice: unitPrice,
          }
        } else if (pricingMethod === 'Manual') {
          updatedItem = {
            ...updatedItem,
            UnitPrice: amount,
          }
        }

        return updatedItem
      }
      return item
    })

    handleBulkEdit(updatedLineItems, true)
  }

  const handleBundleLocal = async (linesToBundle: quoteLineType[]) => {
    const itemsToBundle = linesToBundle.filter(item => item.LineNumber !== -1)
    handleBundle(itemsToBundle, selectedBundle, bundleSelection, bundleDescription)
  }

  return (
    <div
      className="action-bar"
      style={{
        opacity: selectedLineItems.length > 0 ? 1 : 0,
        visibility: selectedLineItems.length > 0 ? 'visible' : 'hidden',
      }}
    >
      <Typography variant="body1">
        {selectedLineItems.length} of {lineItemCount} selected
      </Typography>
      <Divider orientation="vertical" flexItem color="white" />
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<PriceChangeOutlined />}
        onClick={() => setOpenDialog('price')}
      >
        Edit Price
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<LayersOutlined />}
        onClick={() => setOpenDialog('bundle')}
      >
        Bundling
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<EditOutlined />}
        onClick={() => {
          setOpenDialog('edit')
        }}
      >
        Bulk Edit
      </Button>
      <Button
        color="inherit"
        size="large"
        variant="text"
        startIcon={<DeleteOutlined />}
        onClick={() => setOpenDialog('delete')}
      >
        Delete
      </Button>

      <Dialog open={openDialog === 'price'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle variant="h6">Edit Price</DialogTitle>
        <DialogContent className="action-bar-dialog-content">
          <Typography variant="body1">All fields are required.</Typography>
          <TextField
            required
            size="small"
            fullWidth
            label="Pricing Method"
            value={pricingMethod}
            onChange={e => setPricingMethod(e.target.value)}
            select
          >
            <MenuItem value="Cost + %">Cost + %</MenuItem>
            <MenuItem value="Cost + $">Cost + $</MenuItem>
            <MenuItem value="Gross Profit (GP)">Gross Profit (GP)</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
          </TextField>
          {(pricingMethod === 'Cost + %' || pricingMethod === 'Gross Profit (GP)') && (
            <TextField
              required
              size="small"
              fullWidth
              label="Amount (%)"
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
            />
          )}
          {(pricingMethod === 'Cost + $' || pricingMethod === 'Manual') && (
            <CurrencyInput
              required
              value={amount}
              onChange={value => setAmount(value ?? 0)}
              size="small"
              fullWidth
              label="Amount ($)"
            />
          )}
        </DialogContent>
        <DialogActions className="action-bar-dialog-actions">
          <Button onClick={() => setOpenDialog(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleEditPrice(Number(amount))
              setOpenDialog(null)
            }}
            variant="contained"
            color="primary"
            disabled={!pricingMethod}
          >
            Edit Price
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog === 'bundle'}
        onClose={() => {
          setOpenDialog(null)
          resetBundleDialog()
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle variant="h6">{isAddingToExistingBundle ? 'Add to Bundle' : 'Create Bundle'}</DialogTitle>
        <DialogContent className="action-bar-dialog-content">
          <Typography variant="body1">
            Bundle Name is required.
            {selectedLineItems.some(lineItem => lineItem.Bundle)
              ? ' Some of the selected line items are already part of another bundle and will be moved.'
              : ''}
          </Typography>
          <Autocomplete
            freeSolo
            options={bundleOptions}
            value={bundleSelection}
            getOptionLabel={getBundleLabel}
            isOptionEqualToValue={isSameBundleOption}
            size="small"
            fullWidth
            renderInput={params => <TextField {...params} label="Bundle Name" />}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') handleBundleSelectionChange(_, value)
            }}
            onChange={handleBundleSelectionChange}
          />
          <TextField
            size="small"
            fullWidth
            multiline
            label="Bundle Description"
            value={bundleDescription}
            onChange={e => setBundleDescription(e.target.value)}
            disabled={isAddingToExistingBundle}
          />
        </DialogContent>
        <DialogActions className="action-bar-dialog-actions">
          <Button
            onClick={() => {
              setOpenDialog(null)
              resetBundleDialog()
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleBundleLocal(selectedLineItems)
              setOpenDialog(null)
              resetBundleDialog()
            }}
            variant="contained"
            color="primary"
            disabled={!bundleSelection}
          >
            {isAddingToExistingBundle ? 'Add to Bundle' : 'Create Bundle'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteDialog
        open={openDialog === 'delete'}
        onClose={() => setOpenDialog(null)}
        onConfirm={() => handleDelete(selectedLineItems)}
        lineItems={selectedLineItems}
      />

      <Drawer open={openDialog === 'edit'} onClose={() => setOpenDialog(null)} anchor="right">
        <LineEdit onClose={() => setOpenDialog(null)} selectedLineItems={selectedLineItems} quoteId={quoteId ?? ''} />
      </Drawer>
    </div>
  )
}
